import React, { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useNavigate, useSearchParams } from "react-router-dom";

import { ViewIcon, ViewOffIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Center,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  IconButton,
  Image,
  Input,
  InputGroup,
  InputRightElement,
  Link,
  Stack,
  Text,
  useToast,
} from "@chakra-ui/react";

import { useResetPasswordMutation } from "redux/services/apiService";
import { useGetConfigsQuery } from "redux/services/config";

import LoginHero from "./LoginHero";

type ResetPasswordFormData = {
  password: string;
  password_confirmation: string;
};

const PASSWORD_REGEX = {
  uppercase: /[A-Z]/,
  number: /[0-9]/,
};

const ResetPassword: React.FC = () => {
  const { t } = useTranslation("common");
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();

  const email = params.get("email");
  const token = params.get("token");

  const { data: config } = useGetConfigsQuery();
  const logoUrl = useMemo(() => {
    return config?.data?.[0]?.site_logo
      ? `${process.env.REACT_APP_API_URL}/${config.data[0].site_logo}`
      : "";
  }, [config]);

  const [resetPassword, { isLoading: isSubmittingApi }] = useResetPasswordMutation();

  const [showPass, setShowPass] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const {
    handleSubmit,
    register,
    watch,
    reset,
    formState: { errors, isSubmitting, isValid },
  } = useForm<ResetPasswordFormData>({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: { password: "", password_confirmation: "" },
  });

  const passwordValue = watch("password");

  if (!email || !token) {
    return (
      <Flex minH="100vh" align="center" justify="center" bg="white">
        <Text color="red.500">{t("something_went_wrong")}</Text>
      </Flex>
    );
  }

  const onSubmit = async (values: ResetPasswordFormData) => {
    try {
      await resetPassword({
        email,
        token,
        password: values.password,
        password_confirmation: values.password_confirmation,
      }).unwrap();

      toast({
        title: t("password_reset_success"),
        description: t("login_with_new_password"),
        status: "success",
        duration: 4000,
        isClosable: true,
      });

      reset();
      navigate("/login");
    } catch (error: any) {
      toast({
        title: t("reset_password_failed"),
        description:
          error?.data?.errors?.password?.[0] ||
          error?.data?.message ||
          t("something_went_wrong"),
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  return (
    <Flex minH="100vh" bg="white">
      {/* LEFT */}
      <Flex
        flex="1"
        direction="column"
        px={{ base: 6, md: 10, lg: 16 }}
        py={{ base: 8, md: 10 }}
      >
        {/* Logo */}
        <Box mb={{ base: 10, md: 14 }}>
          {logoUrl ? (
            <Image src={logoUrl} maxH="44px" objectFit="contain" />
          ) : (
            <Box h="44px" />
          )}
        </Box>

        {/* Content */}
        <Flex flex="1" align="center" justify="center">
          <Box w="full" maxW="520px">
            <Stack spacing={3} m={0} textAlign="center" align="center">
              <Text fontSize={{ base: "2xl", md: "3xl" }} fontWeight="bold" color="gray.800" m={0}>
                {t("reset_password_title")}
              </Text>
              <Text fontSize="sm" color="gray.500" maxW="420px" mx="auto" lineHeight="tall" m={0}>
                {t("reset_password_hint")}
              </Text>
            </Stack>

            <Box
              border="1px solid"
              borderColor="gray.100"
              borderRadius="12px"
              boxShadow="sm"
              p={{ base: 5, md: 6 }}
            >
              <Stack spacing={4}>
                {/* Password */}
                <FormControl isInvalid={!!errors.password}>
                  <FormLabel fontSize="sm" color="gray.700">
                    {t("new_password")}
                  </FormLabel>

                  <InputGroup>
                    <Input
                      type={showPass ? "text" : "password"}
                      placeholder={t("new_password_placeholder")}
                      h="44px"
                      borderRadius="10px"
                      borderColor="gray.200"
                      _focus={{ borderColor: "red.400", boxShadow: "none" }}
                      {...register("password", {
                        required: t("password_required") as string,
                        minLength: {
                          value: 8,
                          message: t("password_min_8") as string,
                        },
                        validate: {
                          hasUppercase: (value) =>
                            PASSWORD_REGEX.uppercase.test(value) ||
                            (t("password_need_uppercase") as string),
                          hasNumber: (value) =>
                            PASSWORD_REGEX.number.test(value) ||
                            (t("password_need_number") as string),
                        },
                      })}
                    />

                    <InputRightElement h="44px" w="44px">
                      <IconButton
                        aria-label={showPass ? "Hide password" : "Show password"}
                        variant="ghost"
                        size="sm"
                        icon={showPass ? <ViewOffIcon /> : <ViewIcon />}
                        onClick={() => setShowPass((p) => !p)}
                        _hover={{ bg: "transparent" }}
                        _active={{ bg: "transparent" }}
                        _focus={{ boxShadow: "none" }}
                      />
                    </InputRightElement>
                  </InputGroup>

                  <FormErrorMessage>{errors.password?.message}</FormErrorMessage>
                </FormControl>

                {/* Confirm */}
                <FormControl isInvalid={!!errors.password_confirmation}>
                  <FormLabel fontSize="sm" color="gray.700">
                    {t("confirm_password")}
                  </FormLabel>

                  <InputGroup>
                    <Input
                      type={showConfirm ? "text" : "password"}
                      placeholder={t("confirm_password_placeholder")}
                      h="44px"
                      borderRadius="10px"
                      borderColor="gray.200"
                      _focus={{ borderColor: "red.400", boxShadow: "none" }}
                      {...register("password_confirmation", {
                        required: t("password_confirm_required") as string,
                        validate: (val) =>
                          val === passwordValue || (t("password_confirm_mismatch") as string),
                      })}
                    />

                    <InputRightElement h="44px" w="44px">
                      <IconButton
                        aria-label={showConfirm ? "Hide password" : "Show password"}
                        variant="ghost"
                        size="sm"
                        icon={showConfirm ? <ViewOffIcon /> : <ViewIcon />}
                        onClick={() => setShowConfirm((p) => !p)}
                        _hover={{ bg: "transparent" }}
                        _active={{ bg: "transparent" }}
                        _focus={{ boxShadow: "none" }}
                      />
                    </InputRightElement>
                  </InputGroup>

                  <FormErrorMessage>{errors.password_confirmation?.message}</FormErrorMessage>
                </FormControl>

                {/* Button */}
                <Button
                  bg="red.500"
                  color="white"
                  h="44px"
                  borderRadius="10px"
                  onClick={handleSubmit(onSubmit)}
                  isLoading={isSubmitting || isSubmittingApi}
                  isDisabled={!isValid}
                  _hover={{ bg: "red.500" }}
                  _active={{ bg: "red.500" }}
                  _focus={{ boxShadow: "none" }}
                  _disabled={{
                    bg: "gray.200",
                    color: "gray.500",
                    cursor: "not-allowed",
                    _hover: { bg: "gray.200" },
                  }}
                >
                  {t("reset_password_submit")}
                </Button>

                <Center>
                  <Link
                    as={RouterLink}
                    to="/login"
                    fontSize="sm"
                    color="red.500"
                    fontWeight="semibold"
                    _hover={{ textDecoration: "underline" }}
                  >
                    {t("back_to_login")}
                  </Link>
                </Center>
              </Stack>
            </Box>
          </Box>
        </Flex>
      </Flex>

      {/* RIGHT */}
      <LoginHero />
    </Flex>
  );
};

export default ResetPassword;
